import api from '../api';

export interface ProcessStep {
  id: number;
  name: string;
  processStepName?: string;
  description?: string;
  department?: string;
  owner?: string;
}

export interface ComplianceSection {
  applicable?: boolean;
  requirements: string[];
  risk_level: 'low' | 'medium' | 'high';
  notes: string;
  frameworks?: string[];
  audit_trail_needed?: boolean;
  retention_period?: string;
  pii_involved?: boolean;
  data_classification?: string;
  sod_required?: boolean;
  approval_required?: boolean;
  approval_levels?: string[];
  required_docs?: string[];
}

// Enhanced Compliance Interfaces (New 7-Section Format)
export interface SOXControl {
  controlId: string;
  controlName: string;
  category: string;
  description: string;
  whyImpacted: string;
  objectives: string[];
  relevanceScore: number;
}

export interface ControlActivity {
  id: string;
  activity: string;
  controlReference: string;
  category: string;
  frequency: string;
  responsible: string;
  status: 'pending' | 'completed';
  evidenceRequired: string;
}

export interface EnhancedComplianceRequirements {
  sox_controls?: {
    applicable: boolean;
    controls: SOXControl[];
    controlCount: number;
    itGeneralControlsCount: number;
    businessProcessControlsCount: number;
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  control_activities?: {
    applicable: boolean;
    checklist: ControlActivity[];
    totalActivities: number;
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  change_management?: {
    applicable: boolean;
    changeType: string;
    approvalLevels: string[];
    sopReferences: { code: string; name: string; section: string }[];
    trainingRequirements: {
      requiredRoles: string[];
      estimatedUsers: string;
      trainingDuration: string;
      trainingType: string;
    };
    testingPeriod: {
      duration: string;
      phases: { name: string; duration: string }[];
      parallelRunRequired: boolean;
      regressionRequired: boolean;
    };
    notificationRequirements: string[];
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  data_governance?: {
    applicable: boolean;
    dataClassification: string;
    classificationLevel: { name: string; description: string; color: string };
    dataTypesInvolved: { type: string; name: string; category: string }[];
    piiInvolved: boolean;
    financialDataInvolved: boolean;
    privacyRequirements: string[];
    retentionRequirements: { period: string; details: string };
    dataResidency: { considerations: string[]; requiresReview: boolean };
    applicableFrameworks: string[];
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  validation_monitoring?: {
    applicable: boolean;
    preImplementation: {
      tests: { type: string; description: string; threshold: string }[];
      accuracyThreshold: string;
      signOffRequired: boolean;
    };
    pilotPhase: {
      required: boolean;
      duration: string;
      parallelRunRequired: boolean;
      rollbackTestRequired: boolean;
    };
    ongoingMonitoring: {
      kpis: { name: string; description: string; threshold: string; frequency: string; alertThreshold: string }[];
      qualitySampling: { rate: string; frequency: string; reviewedBy: string };
      reviewFrequency: string;
    };
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  risk_mitigation?: {
    applicable: boolean;
    failureScenarios: {
      scenario: string;
      description: string;
      impact: string;
      businessImpact: string;
      probability: string;
      mitigationControls: string[];
      escalationPath: string;
      recoveryTime: string;
      rollbackProcedure: string;
    }[];
    escalationProcedures: {
      levels: { level: number; title: string; authority: string }[];
      timeframes: Record<string, string>;
    };
    rollbackPlan: {
      required: boolean;
      components: string[];
      maxRollbackTime: string;
      approvalRequired: boolean;
    };
    decisionAuthority: { level: number; title: string; authority: string; examples: string[] }[];
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  regulatory_exam_prep?: {
    applicable: boolean;
    auditorQuestions: { category: string; question: string; suggestedResponse: string }[];
    evidenceRequirements: {
      categories: string[];
      items: Record<string, string[]>;
      totalItems: number;
    };
    documentationStandards: {
      format: string[];
      content: string[];
      retention: string[];
    };
    auditReadiness: {
      preparationChecklist: string[];
      documentInventory: { document: string; location: string }[];
      reviewSchedule: string;
    };
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  metadata?: {
    generatedAt: string;
    opportunityId: number;
    automationType: string;
    complexity: string;
    overallRiskLevel: 'low' | 'medium' | 'high';
  };
}

/**
 * Evidence citation persisted on an opportunity. Media citations resolve to a
 * timestamp (transcript window or storyboard frame), text citations to a page.
 */
export interface OpportunityCitation {
  evidence_id: string;
  document_id: number;
  filename: string;
  media_type: 'video' | 'audio' | null;
  kind: 'transcript' | 'screen' | 'text';
  timestamp_start_sec: number | null;
  timestamp_end_sec: number | null;
  timestamp_label: string | null;
  page: number | null;
  excerpt: string;
  quote?: string;
  /** Why this evidence supports the finding (model-provided, persisted). */
  reason?: string;
  /** Deterministic quote check against the excerpt. */
  status?: 'verified' | 'likely' | 'unverified';
}

/** One append-only entry in an opportunity's audit trail. */
export interface DecisionTrailEvent {
  schema_version: string;
  event: 'assessed' | 'dispositioned' | 'override' | 'override_reverted' | 'approval' | 'skill_generated' | 'skill_refused' | string;
  at: string;
  by?: string;
  model_id?: string;
  prompt_version?: string;
  engine_version?: string;
  generator_version?: string;
  inputs_hash?: string;
  citation_set_hash?: string;
  input_hash?: string;
  rule?: string | null;
  hard_stop?: boolean;
  disposition?: string;
  rationale?: string | null;
  pending_approval?: boolean;
  pending_approval_reason?: string | null;
  field?: string;
  from?: any;
  to?: any;
  reason?: string | null;
  gate?: 'mrm' | 'sod' | string;
  status?: string;
  code?: string;
  citations?: string[] | number;
  [key: string]: any;
}

/** A number on the executive story page, with the record/engine it came from. */
export interface StoryFigure {
  value: number | null;
  unit: string | null;
  source: string;
}

export interface StoryCitation {
  evidence_id: string;
  document_id: number;
  filename: string;
  media_type: 'video' | 'audio' | null;
  kind: 'transcript' | 'screen' | 'text';
  locator: string | null;
  timestamp_start_sec: number | null;
  page: number | null;
  quote: string | null;
  reason: string | null;
  status: 'verified' | 'likely' | 'unverified' | null;
}

export interface StoryOpportunityRef {
  id: number;
  title: string;
  disposition: string;
  rule?: string | null;
  rationale?: string | null;
  steps?: string[];
  human_override?: { by: string | null; at: string | null; reason: string | null; from: any; to: any } | null;
  citation_count?: number;
  platform?: string | null;
  complexity?: string | null;
  kept_with?: string[];
  job_story_line?: string | null;
  pending_approval?: boolean;
  pending_approval_reason?: string | null;
}

export interface ExecutiveStory {
  version: string;
  generated_at: string;
  integrity: { opportunity_count: number; opportunity_ids: number[]; inputs_hash: string | null; rule: string };
  situation: {
    project: { id: number; name: string; client_name: string | null; description: string | null };
    assessed_at: string | null;
    assessed_by: string | null;
    model_id: string | null;
    prompt_version: string | null;
    engine_version: string | null;
    inputs_hash: string | null;
    pattern_source: string | null;
    derived_from_evidence: boolean;
    steps_analyzed: StoryFigure;
    opportunities: StoryFigure;
    sources_used: StoryFigure;
    recordings_used: StoryFigure;
  };
  evidence: {
    total_citations: StoryFigure;
    opportunities_with_citations: StoryFigure;
    opportunities_without_citations: StoryFigure;
    status_counts: { verified: number; likely: number; unverified: number; source: string };
    sources: Array<{ document_id: number; filename: string; media_type: string | null; media_duration_sec: number | null; citation_count: number }>;
  };
  dispositions: {
    counts: Record<'automate' | 'skill_gpt' | 'keep_human' | 'do_not_change', StoryFigure>;
    labels: Record<string, string>;
    human_overrides: StoryFigure;
    will_change: StoryOpportunityRef[];
    will_not_use_ai: StoryOpportunityRef[];
  };
  artifacts: {
    skill_bundles: StoryFigure;
    skill_bundles_refused: StoryFigure;
    workflow_blueprints: StoryFigure;
    items: Array<{ id: number; title: string; disposition: string; generated_at: string; citation_count: number }>;
    pending: Array<{ id: number; title: string; disposition: string }>;
  };
  benefits: {
    grounded_opportunities: StoryFigure;
    estimated_opportunities: StoryFigure;
    grounded: null | {
      calculation_version: string | null;
      annual_hours_saved: StoryFigure;
      total_annual_benefit: StoryFigure;
      net_annual_savings: StoryFigure;
      total_implementation_cost: StoryFigure;
      npv_3year: StoryFigure;
      per_opportunity: Array<{ id: number; title: string; annual_savings: number | null; total_annual_benefit: number | null; npv_3year: number | null; payback_months: number | null; roi_3year_pct: number | null; calculated_at: string | null }>;
    };
    rollup: null | {
      source: string;
      total_records: number;
      total_realized: number;
      by_type: Array<{ benefit_type: string; opportunity_count: number; metrics: Array<{ metric_key: string; label: string; unit: string | null; aggregation: 'sum' | 'avg'; projected: number | null; realized: number | null }> }>;
    };
    estimates: Array<{ id: number; title: string; estimated_time_reduction: string; label: string }>;
  };
  controls: {
    opportunities_touching_controls: StoryFigure;
    distinct_sox_controls: StoryFigure;
    control_activities: StoryFigure;
    approvals: { mrm: { approved: StoryFigure; pending: StoryFigure; rejected: StoryFigure }; sod: { approved: StoryFigure; pending: StoryFigure; rejected: StoryFigure } };
    by_opportunity: Array<{ id: number; title: string; disposition: string; sox_controls: Array<{ id: string | null; category: string | null; name: string | null }>; control_activities: Array<{ id: string | null; activity: string | null; status: string | null; control: string | null }> }>;
  };
  change: {
    stakeholders_involved: StoryFigure;
    approvers_required: StoryFigure;
    stakeholder_effort_hours: StoryFigure;
    training_hours: StoryFigure;
    high_risk_flags: StoryFigure;
    stakeholders: Array<{ name: string; role_type: string | null; department: string | null; opportunities: number; approval_required: boolean; effort_hours: number }>;
    risk_flags: Array<{ opportunity_id: number; type: string | null; title: string | null; severity: string | null; description: string | null; mitigation: string | null; affected_groups: string[] }>;
    job_stories: Array<{ opportunity_id: number; title: string; disposition: string; framing: string; roles_affected: string[]; named_owner: string | null; what_human_keeps: string | null; what_skill_takes: string | null; upskilling_path: string[]; plain_language: string | null }>;
  };
  decisions: { count: StoryFigure; items: Array<{ type: string; title: string; opportunity_ids: number[]; source: string }> };
  citations: Array<{ opportunity_id: number; title: string; disposition: string; citations: StoryCitation[] }>;
}

export interface OptimizationOpportunity {
  id: number;
  inefficiency_type: string;
  title: string;
  description: string;
  affected_steps: any[];
  pattern_details: any;
  citations?: OpportunityCitation[];
  decision_trail?: DecisionTrailEvent[];
  disposition?: 'automate' | 'skill_gpt' | 'keep_human' | 'do_not_change' | string;
  disposition_rationale?: string | null;
  disposition_hard_stop?: string | null;
  disposition_pending_approval?: boolean;
  disposition_pending_approval_reason?: string | null;
  solution_approach: string;
  implementation_complexity: 'low' | 'medium' | 'high';
  estimated_time_reduction: string;
  regulatory_considerations: string;
  primary_tools: string[];
  alternative_tools: string[];
  integration_requirements: Array<string | { system: string; access_type?: string }>;
  ai_integration_opportunities: string;
  ai_regulatory_notes: string;
  compliance_requirements?: EnhancedComplianceRequirements | {
    // Legacy format support
    sox_controls: ComplianceSection;
    regulatory_frameworks: ComplianceSection;
    audit_requirements: ComplianceSection;
    data_privacy: ComplianceSection;
    segregation_of_duties: ComplianceSection;
    change_management: ComplianceSection;
    documentation: ComplianceSection;
  };
  workflow_count: number;
  governance_complexity_score?: number;
  automation_readiness_score?: number;
  integration_complexity_score?: number;
  regulatory_risk_score?: number;
  recommended_platform?: string;
  recommended_approach?: string;
  integration_analysis?: {
    total_integrations: number;
    integrations: Array<{
      system: string;
      api_available: boolean;
      connector_available: boolean;
      auth_methods: string[];
      typical_approval_time: string;
      governance_complexity: string;
      recommendation: string;
    }>;
  };
  governance_analysis?: any;
  phased_rollout_plan?: {
    total_phases: number;
    recommended_starting_phase: number;
    phase_1?: any;
    phase_2?: any;
    phase_3?: any;
  };
  dependencies?: {
    prerequisites: Array<{
      id: number;
      dependency_type: string;
      dependency_strength: 'required' | 'recommended' | 'optional';
      reason: string;
      prerequisite_opportunity?: {
        id: number;
        title: string;
      };
    }>;
    blocks: Array<{
      id: number;
      dependency_type: string;
      dependency_strength: 'required' | 'recommended' | 'optional';
      reason: string;
      blocked_opportunity?: {
        id: number;
        title: string;
      };
    }>;
  };
  // Grounded Calculation Engine results
  data_source?: 'estimated' | 'grounded';
  annual_hours_saved?: number;
  weighted_hourly_rate?: number;
  annual_dollar_value?: number;
  error_reduction_value?: number;
  total_annual_benefit?: number;
  annual_savings?: number;
  total_implementation_cost?: number;
  npv_3year?: number;
  roi_3year_pct?: number;
  roi_percentage?: number;
  payback_months?: number;
  calculated_at?: string;
  grounded_date?: string | null;
  // Monte Carlo Sensitivity Analysis results
  mc_npv_p10?: number;
  mc_npv_p50?: number;
  mc_npv_p90?: number;
  mc_roi_p10?: number;
  mc_roi_p50?: number;
  mc_roi_p90?: number;
  mc_payback_p10?: number;
  mc_payback_p50?: number;
  mc_payback_p90?: number;
  mc_prob_positive_roi?: number;
  mc_prob_payback_12mo?: number;
  mc_histogram_data?: {
    npv: { bin_start: number; bin_end: number; count: number }[];
    roi: { bin_start: number; bin_end: number; count: number }[];
    payback: { bin_start: number; bin_end: number; count: number }[];
  };
  mc_n_simulations?: number;
  mc_calculated_at?: string;
  // Approval Status
  mrm_approval_status?: 'pending' | 'approved' | 'rejected';
  mrm_approval_date?: string;
  mrm_approved_by?: string;
  sod_approval_status?: 'pending' | 'approved' | 'rejected';
  sod_approval_date?: string;
  sod_approved_by?: string;
  manual_rank?: number | null;
  // Imported metrics data
  current_volume?: number | null;
  current_time_per_unit?: number | null;
  projected_time_per_unit?: number | null;
  time_savings_percentage?: number | null;
  implementation_cost?: number | null;
  annual_maintenance_cost?: number | null;
  // ROI calculations
  roi_data?: {
    annual_savings: number;
    total_implementation_cost: number;
    roi_percentage: number;
    payback_months: number | null;
    net_present_value: number | null;
  } | null;
  // Architecture and Data Flow
  architecture_diagram?: ArchitectureDiagram;
  data_flow_map?: DataFlowMap;
  integration_complexity?: IntegrationComplexity;
  // Stakeholder & Change Management
  stakeholder_analysis?: {
    stakeholders: Array<{
      id: string;
      name: string;
      department: string;
      role_type: string;
      responsible: boolean;
      accountable: boolean;
      consulted: boolean;
      informed: boolean;
      reasoning: string;
      approval_required: boolean;
      estimated_effort_hours?: number;
    }>;
    approval_chain: string[];
    bottleneck_risk: 'low' | 'medium' | 'high';
    total_approvers: number;
    cross_department_count: number;
    estimated_approval_days: number;
  };
  change_impact_data?: {
    roles_affected: number;
    departments_affected: string[];
    training_level: 'Low' | 'Medium' | 'High';
    training_requirements: Array<{
      role: string;
      training_type: string;
      estimated_hours: number;
      delivery_method: string;
    }>;
    estimated_training_hours: number;
    communication_triggers: string[];
    rollout_recommendation: 'big_bang' | 'phased' | 'pilot';
    change_velocity: 'slow' | 'moderate' | 'fast';
    support_requirements: {
      tier1_volume_increase: string;
      documentation_updates: number;
      faq_items_needed: number;
    };
  };
  resistance_risk_flags?: Array<{
    id: string;
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    mitigation: string;
    affected_groups: string[];
  }>;
}

export interface ArchitectureDiagram {
  systems: Array<{
    id: string;
    name: string;
    type: 'source' | 'target' | 'middleware' | 'ui' | 'database' | 'api';
    category: string;
    description: string;
    position: { x: number; y: number };
  }>;
  connections: Array<{
    id: string;
    from: string;
    to: string;
    dataType: string;
    protocol: string;
    direction: 'unidirectional' | 'bidirectional';
    description: string;
  }>;
  integrationPoints: Array<{
    id: string;
    system: string;
    complexity: 'simple' | 'moderate' | 'complex';
    authMethod: string;
    rateLimit: string;
    errorHandling: string;
    constraints: string[];
  }>;
  securityBoundaries?: Array<{
    id: string;
    name: string;
    systems: string[];
    accessControl: string;
  }>;
}

export interface DataFlowMap {
  source: {
    system: string;
    description: string;
  };
  transformations: Array<{
    step: string;
    system: string;
    description: string;
  }>;
  storage: Array<{
    system: string;
    type: 'temporary' | 'permanent';
    retention: string;
  }>;
  accessControl: Array<{
    point: string;
    roles: string[];
    permissions: string;
  }>;
}

export interface IntegrationComplexity {
  totalScore: number;
  simpleCount: number;
  moderateCount: number;
  complexCount: number;
  integrations: Array<{
    id: string;
    system: string;
    complexity: 'simple' | 'moderate' | 'complex';
    authMethod: string;
    rateLimit: string;
    errorHandling: string;
    constraints: string[];
  }>;
}

export interface OptimizationAnalysis {
  analysis: {
    id: number;
    project_id: number;
    total_steps: number;
    inefficiency_count: number;
    automation_opportunities: number;
    patterns_detected: any;
    analysis_date: string;
  };
  opportunities: OptimizationOpportunity[];
}

export interface ProcessSkillStep {
  step_number: number | null;
  name: string;
  actor: string | null;
  run_by: 'agent_skill' | 'system_step' | 'human_checkpoint' | 'human_step';
  handoff?: boolean;
  sub_skill?: string;
  opportunity_id: number | null;
  note: string | null;
}

export interface ProcessSkillPreview {
  process_name: string;
  status: 'approved' | 'draft';
  process_skill_version: string;
  generated_at: string;
  analysis_id: number;
  input_hash: string;
  step_order_source: 'process_map' | 'opportunities';
  counts: { agent_skill: number; system_step: number; human_checkpoint: number; human_step: number };
  steps: ProcessSkillStep[];
  pending_sub_skills: { opportunity_id: number; title: string; reason: string }[];
  unapproved_sub_skills: { opportunity_id: number; title: string; skill_status: string }[];
  files: string[];
}

export interface WorkflowBlueprint {
  id: number;
  workflow_name: string;
  workflow_type: string;
  description: string;
  trigger_config: any;
  workflow_steps: any[];
  conditions: any[];
  integrations: string[];
  prerequisites: string[];
  step_by_step_instructions: any[];
  troubleshooting_tips: any[];
  external_resources: any[];
}

export interface AIAssistantSession {
  sessionId: string;
  context: any;
}

export interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  images?: string[];
  timestamp: string;
}

/**
 * Process Optimization API Service
 */
class ProcessOptimizationAPI {
  /**
   * Run optimization analysis for a project
   */
  async analyzeProject(
    projectId: number,
    processSteps?: ProcessStep[],
    forceRegenerate = false,
    options?: { verify?: boolean; mode?: 'off' | 'standard' | 'deep'; ignoreHashGate?: boolean }
  ): Promise<{
    success: boolean;
    data: OptimizationAnalysis;
    fromCache?: boolean;
    verification_metadata?: any;
    /** Hash gate outcome: 'unchanged' means the previous assessment was reused because inputs did not change. */
    hash_gate?: { status: 'unchanged' | 'new_inputs' | 'bypassed'; inputs_hash: string; analysis_id?: number; assessed_at?: string };
    ai_model_used?: string;
  }> {
    const mode = options?.mode || (options?.verify ? 'standard' : 'off');
    const response = await api.post(`/api/process-optimization/${projectId}/analyze`, {
      processSteps,
      forceRegenerate,
      ignoreHashGate: !!options?.ignoreHashGate,
      verify: mode !== 'off',
      verification_mode: mode
    }, {
      timeout: 300000
    });
    return response.data;
  }

  /**
   * Executive story: one narrative assembled from persisted records and the
   * deterministic benefit/ROI engines. Every figure carries its `source`.
   */
  async getExecutiveStory(projectId: number): Promise<{ success: boolean; data?: ExecutiveStory; needsGeneration?: boolean }> {
    try {
      const response = await api.get(`/api/process-optimization/${projectId}/executive-story`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return { success: false, needsGeneration: true };
      throw error;
    }
  }

  /**
   * Persist a human disposition override (recorded on the decision trail with the reason).
   * Pass `null` to revert to the engine's disposition.
   */
  async updateDisposition(
    projectId: number,
    opportunityId: number,
    disposition: 'automate' | 'skill_gpt' | 'keep_human' | 'do_not_change' | null,
    reason?: string
  ): Promise<{ id: number; disposition: string; disposition_rationale: string | null; disposition_hard_stop: string | null; decision_trail: DecisionTrailEvent[] }> {
    const response = await api.patch(`/api/process-optimization/${projectId}/opportunities/${opportunityId}/disposition`, {
      disposition,
      reason
    });
    return response.data.data;
  }

  /**
   * Get optimization analysis for a project
   */
  async getAnalysis(projectId: number): Promise<{ success: boolean; data?: OptimizationAnalysis; needsGeneration?: boolean }> {
    try {
      const response = await api.get(`/api/process-optimization/${projectId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          needsGeneration: true
        };
      }
      throw error;
    }
  }

  /**
   * Get workflow blueprints for an opportunity
   * @param opportunityId - ID of the opportunity
   * @param generateIfMissing - Whether to generate workflows if they don't exist (default: true)
   * @param useAI - Whether to use AI for detailed instructions (default: true)
   * @param forceRegenerate - Whether to delete existing and regenerate (default: false)
   */
  async getWorkflows(opportunityId: number, generateIfMissing = true, useAI = true, forceRegenerate = false): Promise<WorkflowBlueprint[]> {
    const response = await api.get(`/api/process-optimization/opportunity/${opportunityId}/workflows`, {
      params: {
        generateIfMissing: generateIfMissing ? 'true' : 'false',
        useAI: useAI ? 'true' : 'false',
        forceRegenerate: forceRegenerate ? 'true' : 'false'
      },
      timeout: useAI ? 600000 : 30000 // 10 minutes for AI, 30 seconds for templates
    });
    return response.data.data;
  }

  /**
   * Generate skill bundle for an opportunity (SKILL.md + GPT spec + check.js)
   */
  async generateSkillBundle(opportunityId: number): Promise<{
    input_hash: string;
    citation_set_hash?: string;
    citation_count?: number;
    unverified_citation_count?: number;
    generator_version?: string;
    generated_at: string;
    opportunity_id: number;
    disposition: string;
    skill_md: string;
    gpt_spec: object;
    check_script: string;
  }> {
    // A 422 with code CITATIONS_REQUIRED means the generator refused (fail closed):
    // the opportunity has no evidence citations. Callers surface `error.response.data.message`.
    const response = await api.post(`/api/process-optimization/opportunity/${opportunityId}/skill-bundle`);
    return response.data.data;
  }

  /**
   * Compliance ship gate: approve a generated skill bundle. A 422 with
   * blocked:true carries deterministic failures, each citing a catalogued
   * control ID where one applies.
   */
  async approveSkill(opportunityId: number): Promise<{
    skill_status: string;
    skill_approved_at: string;
    skill_approved_by: string;
    mrm_tier: string | null;
    gate_version: string;
  }> {
    const response = await api.post(`/api/process-optimization/opportunity/${opportunityId}/skill-approval`);
    return response.data.data;
  }

  /**
   * Skill registry: every generated skill for a project with owner, version,
   * status, and deterministic staleness against current process docs.
   */
  async getSkillRegistry(projectId: number): Promise<{
    id: number;
    title: string;
    disposition: string;
    skill_status: string;
    skill_owner: string | null;
    skill_version: number;
    skill_bundle_generated_at: string | null;
    skill_approved_at: string | null;
    skill_approved_by: string | null;
    skill_last_reviewed_at: string | null;
    input_hash: string | null;
    generator_version: string | null;
    stale: boolean;
    stale_reasons: string[];
    history: { event: string; at: string; by?: string; [key: string]: unknown }[];
  }[]> {
    const response = await api.get(`/api/process-optimization/${projectId}/skill-registry`);
    return response.data.data;
  }

  async getProcessSkill(projectId: number): Promise<ProcessSkillPreview> {
    const response = await api.get(`/api/process-optimization/${projectId}/process-skill`);
    return response.data.data;
  }

  async downloadProcessSkill(projectId: number): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get(`/api/process-optimization/${projectId}/process-skill/download`, { responseType: 'blob' });
    const disposition = String(response.headers?.['content-disposition'] || '');
    const match = disposition.match(/filename="([^"]+)"/);
    return { blob: response.data, filename: match ? match[1] : 'process-skill.zip' };
  }

  /**
   * Skill governance updates: owner reassignment, review stamp, deprecate or
   * restore to draft. Approval only ever happens through the ship gate.
   */
  async updateSkillGovernance(opportunityId: number, updates: { owner?: string; status?: 'draft' | 'deprecated'; mark_reviewed?: boolean }): Promise<{
    id: number;
    skill_status: string;
    skill_owner: string | null;
    skill_version: number;
    skill_last_reviewed_at: string | null;
  }> {
    const response = await api.patch(`/api/process-optimization/opportunity/${opportunityId}/skill-governance`, updates);
    return response.data.data;
  }

  /**
   * Fetch previously generated skill bundle
   */
  async getSkillBundle(opportunityId: number): Promise<{
    input_hash: string;
    generated_at: string;
    skill_md: string;
    gpt_spec: object;
    check_script: string;
  } | null> {
    try {
      const response = await api.get(`/api/process-optimization/opportunity/${opportunityId}/skill-bundle`);
      return response.data.data;
    } catch {
      return null;
    }
  }

  /**
   * Create AI assistant session
   */
  async createAssistantSession(opportunityId: number, workflowBlueprintId?: number): Promise<AIAssistantSession> {
    const response = await api.post('/api/process-optimization/assistant/session', {
      opportunityId,
      workflowBlueprintId
    });
    return response.data.data;
  }

  /**
   * Send message to AI assistant
   */
  async sendMessage(sessionId: string, message: string, images?: string[]): Promise<{ message: string; sessionId: string }> {
    const response = await api.post('/api/process-optimization/assistant/message', {
      sessionId,
      message,
      images
    }, {
      timeout: 300000 // 5 minutes timeout for AI responses
    });
    return response.data.data;
  }

  /**
   * Get assistant session details
   */
  async getSession(sessionId: string) {
    const response = await api.get(`/api/process-optimization/assistant/session/${sessionId}`);
    return response.data.data;
  }

  /**
   * Update session context
   */
  async updateContext(sessionId: string, contextUpdates: any) {
    const response = await api.patch(`/api/process-optimization/assistant/session/${sessionId}/context`, {
      contextUpdates
    });
    return response.data;
  }

  /**
   * Delete analysis
   */
  async deleteAnalysis(analysisId: number) {
    const response = await api.delete(`/api/process-optimization/${analysisId}`);
    return response.data;
  }

  /**
   * Download PowerPoint deliverable for a project
   */
  async downloadPptx(projectId: number, projectName?: string): Promise<void> {
    const response = await api.post(`/api/export/optimization/${projectId}/pptx`, {}, {
      responseType: 'blob',
      timeout: 60000
    });
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().split('T')[0];
    a.download = `${projectName ? projectName.replace(/[^a-z0-9]/gi, '_') : 'Process_Optimization'}_${date}.pptx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const processOptimizationAPI = new ProcessOptimizationAPI();
